import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { TourPlacement, TourStep } from '../services/tour';

const BACKDROP_COLOR = 'rgba(15, 23, 42, 0.48)';
const HIGHLIGHT_PADDING = 8;
const HIGHLIGHT_RADIUS = 10;
const POPOVER_MAX_WIDTH = 340;
const POPOVER_GAP = 16;
const POPOVER_MARGIN = 12;
const ARROW_SIZE = 10;

interface PositionedRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ResolvedLayout {
  highlight: PositionedRect | null;
  popover: { top: number; left: number; width: number; height: number };
  placement: TourPlacement;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function getViewportSize(): { w: number; h: number } {
  return { w: window.innerWidth, h: window.innerHeight };
}

function measurePopover(popover: HTMLDivElement | null): { width: number; height: number } {
  if (!popover) return { width: POPOVER_MAX_WIDTH, height: 160 };
  const rect = popover.getBoundingClientRect();
  return { width: rect.width, height: rect.height };
}

function resolveLayout(
  step: TourStep,
  popoverSize: { width: number; height: number }
): ResolvedLayout {
  const { w: vw, h: vh } = getViewportSize();
  const popoverWidth = Math.min(POPOVER_MAX_WIDTH, vw - POPOVER_MARGIN * 2);
  const popoverHeight = popoverSize.height || 180;

  const target = step.target ? (document.querySelector(step.target) as HTMLElement | null) : null;
  let highlight: PositionedRect | null = null;
  if (target) {
    const r = target.getBoundingClientRect();
    const inView = r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
    if (inView) {
      highlight = {
        top: r.top - HIGHLIGHT_PADDING,
        left: r.left - HIGHLIGHT_PADDING,
        width: r.width + HIGHLIGHT_PADDING * 2,
        height: r.height + HIGHLIGHT_PADDING * 2,
      };
    }
  }

  if (step.placement === 'center' || !step.target) {
    return {
      highlight,
      popover: {
        width: popoverWidth,
        height: popoverHeight,
        top: clamp((vh - popoverHeight) / 2, POPOVER_MARGIN, vh - popoverHeight - POPOVER_MARGIN),
        left: clamp((vw - popoverWidth) / 2, POPOVER_MARGIN, vw - popoverWidth - POPOVER_MARGIN),
      },
      placement: 'center',
    };
  }

  const spaceTop = highlight ? highlight.top : vh;
  const spaceBottom = highlight ? vh - (highlight.top + highlight.height) : vh;
  const spaceLeft = highlight ? highlight.left : vw;
  const spaceRight = highlight ? vw - (highlight.left + highlight.width) : vw;

  let placement: TourPlacement = step.placement;
  if (placement === 'top' && spaceTop < popoverHeight + POPOVER_GAP) placement = 'bottom';
  if (placement === 'bottom' && spaceBottom < popoverHeight + POPOVER_GAP) placement = 'top';
  if (placement === 'left' && spaceLeft < popoverWidth + POPOVER_GAP) placement = 'right';
  if (placement === 'right' && spaceRight < popoverWidth + POPOVER_GAP) placement = 'left';

  let top = 0;
  let left = 0;
  if (highlight) {
    if (placement === 'top') {
      top = highlight.top - popoverHeight - POPOVER_GAP;
    } else if (placement === 'bottom') {
      top = highlight.top + highlight.height + POPOVER_GAP;
    } else if (placement === 'left') {
      top = highlight.top + highlight.height / 2 - popoverHeight / 2;
      left = highlight.left - popoverWidth - POPOVER_GAP;
    } else if (placement === 'right') {
      top = highlight.top + highlight.height / 2 - popoverHeight / 2;
      left = highlight.left + highlight.width + POPOVER_GAP;
    }

    if (placement === 'top' || placement === 'bottom') {
      const desiredCenter = highlight.left + highlight.width / 2;
      left = clamp(desiredCenter - popoverWidth / 2, POPOVER_MARGIN, vw - popoverWidth - POPOVER_MARGIN);
    }
  } else {
    top = clamp((vh - popoverHeight) / 2, POPOVER_MARGIN, vh - popoverHeight - POPOVER_MARGIN);
    left = clamp((vw - popoverWidth) / 2, POPOVER_MARGIN, vw - popoverWidth - POPOVER_MARGIN);
  }

  return {
    highlight,
    popover: {
      top: clamp(top, POPOVER_MARGIN, vh - popoverHeight - POPOVER_MARGIN),
      left: clamp(left, POPOVER_MARGIN, vw - popoverWidth - POPOVER_MARGIN),
      width: popoverWidth,
      height: popoverHeight,
    },
    placement,
  };
}

interface TourOverlayProps {
  step: TourStep | null;
  stepIndex: number;
  totalSteps: number;
  isLast: boolean;
  onNext: () => void;
  onPrev: () => void;
  onDismiss: () => void;
}

export default function TourOverlay({
  step,
  stepIndex,
  totalSteps,
  isLast,
  onNext,
  onPrev,
  onDismiss,
}: TourOverlayProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<ResolvedLayout | null>(null);

  useLayoutEffect(() => {
    if (!step) {
      setLayout(null);
      return;
    }
    const popoverSize = measurePopover(popoverRef.current);
    setLayout(resolveLayout(step, popoverSize));
  }, [step]);

  useEffect(() => {
    if (!step) return;
    const recompute = () => {
      const popoverSize = measurePopover(popoverRef.current);
      setLayout(resolveLayout(step, popoverSize));
    };
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [step]);

  useEffect(() => {
    if (!step) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, onNext, onPrev, onDismiss]);

  useEffect(() => {
    if (!step) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [step]);

  if (!step || !layout) return null;

  const progressLabel = `${Math.max(1, stepIndex + 1)} of ${totalSteps}`;
  const isFirst = stepIndex <= 0;
  const highlightTop = layout.highlight?.top ?? 0;
  const highlightLeft = layout.highlight?.left ?? 0;
  const highlightWidth = layout.highlight?.width ?? 0;
  const highlightHeight = layout.highlight?.height ?? 0;

  return (
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Site tour">
      <div
        className="absolute inset-0"
        style={{ background: BACKDROP_COLOR }}
        onClick={onDismiss}
        data-testid="tour-backdrop"
      />

      {layout.highlight && (
        <div
          aria-hidden="true"
          data-testid="tour-highlight"
          style={{
            position: 'absolute',
            top: highlightTop,
            left: highlightLeft,
            width: highlightWidth,
            height: highlightHeight,
            borderRadius: HIGHLIGHT_RADIUS,
            boxShadow: `0 0 0 9999px ${BACKDROP_COLOR}`,
            background: 'transparent',
            pointerEvents: 'none',
          }}
        />
      )}

      <div
        style={{
          position: 'absolute',
          top: layout.popover.top,
          left: layout.popover.left,
          width: layout.popover.width,
        }}
      >
        {layout.placement !== 'center' && (
          <span
            aria-hidden="true"
            data-testid="tour-arrow"
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              ...(layout.placement === 'right' && {
                left: -ARROW_SIZE,
                borderTop: `${ARROW_SIZE}px solid transparent`,
                borderBottom: `${ARROW_SIZE}px solid transparent`,
                borderRight: `${ARROW_SIZE}px solid white`,
              }),
              ...(layout.placement === 'left' && {
                right: -ARROW_SIZE,
                borderTop: `${ARROW_SIZE}px solid transparent`,
                borderBottom: `${ARROW_SIZE}px solid transparent`,
                borderLeft: `${ARROW_SIZE}px solid white`,
              }),
              ...(layout.placement === 'top' && {
                left: '50%',
                top: 'auto',
                bottom: -ARROW_SIZE,
                transform: 'translateX(-50%)',
                borderLeft: `${ARROW_SIZE}px solid transparent`,
                borderRight: `${ARROW_SIZE}px solid transparent`,
                borderTop: `${ARROW_SIZE}px solid white`,
              }),
              ...(layout.placement === 'bottom' && {
                left: '50%',
                top: -ARROW_SIZE,
                transform: 'translateX(-50%)',
                borderLeft: `${ARROW_SIZE}px solid transparent`,
                borderRight: `${ARROW_SIZE}px solid transparent`,
                borderBottom: `${ARROW_SIZE}px solid white`,
              }),
              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.08))',
            }}
          />
        )}
        <div
          ref={popoverRef}
          role="document"
          data-testid="tour-popover"
          data-step-id={step.id}
          data-placement={layout.placement}
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
        >
          <div className="px-5 pt-4 pb-2 flex items-start justify-between gap-3">
            <h2 className="font-semibold text-gray-900 text-base leading-snug">{step.title}</h2>
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Close tour"
              className="text-gray-400 hover:text-gray-700 text-xl leading-none -mt-1 -mr-1 p-1"
            >
              &times;
            </button>
          </div>
          <div className="px-5 pb-3 text-sm text-gray-600 leading-relaxed">{step.body}</div>
          <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between gap-2">
            <span className="text-xs text-gray-500" data-testid="tour-progress">
              {progressLabel}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onDismiss}
                className="text-xs px-3 py-1.5 text-gray-600 hover:text-gray-800"
              >
                Skip tour
              </button>
              {!isFirst && (
                <button
                  type="button"
                  onClick={onPrev}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={onNext}
                data-testid="tour-next"
                className="text-xs px-3 py-1.5 rounded-lg bg-parksy-600 hover:bg-parksy-700 text-white font-medium"
              >
                {isLast ? 'Got it' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
